import { useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import Sidebar from "@/components/Sidebar";
// HubDashboard.jsx (NOT Dashboard.js — that's the retired Content Engine, kept archived;
// webpack resolves .js before .jsx, so the new screen needs its own basename)
import Dashboard from "@/components/HubDashboard";
import SocialStudio from "@/components/SocialStudio";
import NewsletterStudio from "@/components/NewsletterStudio";
import EmailStudio from "@/components/EmailStudio";
import ContentCalendar from "@/components/ContentCalendar";
import Analytics from "@/components/Analytics";
import GoogleInsights from "@/components/GoogleInsights";
import Subscribers from "@/components/Subscribers";
import Settings from "@/components/Settings";
import PromoteFitters from "@/components/PromoteFitters";
import FitterPacks from "@/components/FitterPacks";
import Referrals from "@/components/Referrals";
import ProofBank from "@/components/ProofBank";
import SurveyResults from "@/components/SurveyResults";
import LpgaApplications from "@/components/LpgaApplications";
import CustomerReplies from "@/components/CustomerReplies";
import Inventory from "@/components/Inventory";
import LeadsTracker from "@/components/LeadsTracker";
import LeadFiles from '@/components/LeadFiles';
import Compose from '@/components/Compose';
import DripSchedule from "@/components/DripSchedule";
import PartnerLeads from "@/components/PartnerLeads";
import FacilityOnboarding from "@/components/FacilityOnboarding";
import SystemsWatchlist from "@/components/SystemsWatchlist";
import PlanningStrip from "@/components/PlanningStrip";
import Offboarding from "@/components/Offboarding";
import HGCFO from "@/components/HGCFO";
import FitterCheckin from "@/components/FitterCheckin";
import OnboardFitter from "@/components/OnboardFitter";
import AgreementInbox from "@/components/AgreementInbox";
import AgreementArchive from "@/components/AgreementArchive";
import ReturnsInbox from "@/components/ReturnsInbox";
import Reviews from "@/components/Reviews";
import NotificationBell from "@/components/NotificationBell";
import Login from "@/components/Login";
import PublicReviewForm from "@/components/PublicReviewForm";
import FitterSigning from "@/components/FitterSigning";
import CardSetup from "@/components/CardSetup";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Menu, X, Loader2 } from "lucide-react";

// Content Engine ("dashboard") + Campaign Planner ("campaigns") retired 2026-07-27 —
// UI only; their data (content_queue, campaign plans) stays in the DB and the backend
// routes remain. HG Templates + Newsletter Archive relocated into Newsletter Studio.
const MODULE_LABELS = {
  dashboard: "Dashboard",
  social: "Social Studio",
  newsletter: "Newsletter Studio",
  email: "Email Studio",
  promote: "Promote Fitters",
  "fitter-packs": "Fitter Packs",
  "lead-files": "Lead Files",
  compose: "Compose",
  referrals: "Referrals",
  "proof-bank": "Proof Bank",
  "survey-results": "Survey Results",
  "lpga-applications": "LPGA Applications",
  "customer-replies": "Customer Replies",
  "leads-tracker": "Leads Tracker",
  planning: "Planning",
  "drip-schedule": "Drip Schedule",
  partners: "Facility Partners",
  "facility-onboarding": "Facility Onboarding",
  watchlist: "Systems Watchlist",
  "offboarding": "Offboarding",
  "fitter-checkin": "Fitter Check-in",
  onboard: "Onboard Fitter",
  approvals: "Approvals",
  agreements: "Agreements",
  returns: "Returns",
  reviews: "Reviews",
  subscribers: "Subscribers",
  calendar: "Content Calendar",
  analytics: "Analytics",
  google: "Google",
  settings: "Settings",
  cfo: "CFO",
};

function HubApp() {
  const { user, logout } = useAuth();
  const [activeModule, setActiveModule] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [emailTab, setEmailTab] = useState(null); // deep-link a sub-tab in Email Studio
  const [promoteTab, setPromoteTab] = useState(null); // ...and in Promote Fitters (the dashboard's promote requests row)
  // CFO tab is allowlist-gated. This probe only controls whether the tab RENDERS —
  // every /api/cfo/* route re-checks the allowlist server-side regardless.
  const [cfoAllowed, setCfoAllowed] = useState(false);
  // Flag-gated surfaces. A nav item that renders and then 404s looks like a broken
  // page; an absent one reads as "not turned on". So the nav asks the server.
  const [features, setFeatures] = useState({});
  const [composeTo, setComposeTo] = useState('');
  const [facilityId, setFacilityId] = useState(null); // opened from a partner lead's Start onboarding
  useEffect(() => {
    axios.get("/api/cfo/access").then(() => setCfoAllowed(true)).catch(() => setCfoAllowed(false));
    // Unreachable or errored => {} => every flag falsy => nothing extra in the nav.
    // Failing closed here is right: showing a surface that is off is the worse error.
    axios.get("/api/features").then((r) => setFeatures(r.data || {})).catch(() => setFeatures({}));
  }, []);

  const handleModuleChange = (id, opts) => {
    if (id === "email" && opts?.tab) setEmailTab(opts.tab);
    if (id === "promote") setPromoteTab(opts?.tab || null);
    setActiveModule(id);
    setSidebarOpen(false);
  };

  // Deep-link from the daily digest email: hub.../?module=approvals opens that screen.
  // ?tab= deep-links a sub-tab (e.g. ?module=email&tab=letters); ?view= is read by
  // the target component itself at mount (e.g. Reviews' awaiting-reply filter).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const m = p.get("module");
    if (m && Object.prototype.hasOwnProperty.call(MODULE_LABELS, m)) {
      if (m === "email" && p.get("tab")) setEmailTab(p.get("tab"));
      if (m === "promote" && p.get("tab")) setPromoteTab(p.get("tab"));
      setActiveModule(m);
    }
  }, []);

  const renderModule = () => {
    switch (activeModule) {
      case "dashboard":
        return <Dashboard onNavigate={handleModuleChange} />;
      case "social":
        return <SocialStudio />;
      case "newsletter":
        return <NewsletterStudio onNavigate={handleModuleChange} />;
      case "email":
        return <EmailStudio onNavigate={handleModuleChange} initialTab={emailTab} />;
      case "promote":
        return <PromoteFitters onNavigate={handleModuleChange} initialTab={promoteTab} />;
      case "fitter-packs":
        return <FitterPacks />;
      case "referrals":
        return <Referrals />;
      case "proof-bank":
        return <ProofBank />;
      case "survey-results":
        return <SurveyResults />;
      case "lpga-applications":
        return <LpgaApplications />;
      case "customer-replies":
        return <CustomerReplies />;
      case "inventory":
        return <Inventory />;
      case "leads-tracker":
        return <LeadsTracker />;
      case "drip-schedule":
        return <DripSchedule />;
      case "partners":
        return <PartnerLeads onStartOnboarding={(fid) => { setFacilityId(fid); setActiveModule("facility-onboarding"); }} />;
      case "facility-onboarding":
        return <FacilityOnboarding openId={facilityId} />;
      case "lead-files":
        return features.lead_files ? <LeadFiles onWrite={features.compose ? (em) => { setComposeTo(em); setActiveModule('compose'); } : undefined} /> : <Dashboard onNavigate={handleModuleChange} />;
      case "compose":
        return features.compose ? <Compose initialEmail={composeTo} /> : <Dashboard onNavigate={handleModuleChange} />;
      case "watchlist":
        return <SystemsWatchlist onNavigate={handleModuleChange} />;
      case "offboarding":
        return <Offboarding />;
      case "fitter-checkin":
        return <FitterCheckin />;
      case "onboard":
        return <OnboardFitter />;
      case "approvals":
        return <AgreementInbox />;
      // Its own screen, not a tab on Approvals: a queue is emptied, an archive is kept.
      case "agreements":
        return <AgreementArchive />;
      case "returns":
        return <ReturnsInbox />;
      case "reviews":
        return <Reviews />;
      case "subscribers":
        return <Subscribers />;
      case "calendar":
        return <ContentCalendar onNavigate={handleModuleChange} />;
      case "planning":
        return <PlanningStrip onNavigate={handleModuleChange} />;
      case "analytics":
        return <Analytics />;
      case "google":
        return <GoogleInsights onNavigate={handleModuleChange} />;
      case "cfo":
        return cfoAllowed ? <HGCFO showCard={!!features.cfo_card} /> : <SocialStudio />;
      case "settings":
        return <Settings />;
      default:
        return <SocialStudio />;
    }
  };

  return (
    <div data-testid="app-root" className="flex min-h-screen bg-hg-bg">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className={`fixed inset-y-0 left-0 z-50 lg:static lg:block ${sidebarOpen ? 'block' : 'hidden lg:block'}`}>
        <Sidebar activeModule={activeModule} onModuleChange={handleModuleChange} user={user} onLogout={logout} showCfo={cfoAllowed} showLeadFiles={!!features.lead_files} showCompose={!!features.compose} />
      </div>

      <main className="flex-1 min-h-screen w-full">
        <header className="h-14 border-b border-hg-border bg-hg-bg/95 backdrop-blur flex items-center px-4 lg:px-8 gap-3 sticky top-0 z-30">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-1.5 text-hg-text-dim hover:text-white transition-colors"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-[0.2em]">
            {MODULE_LABELS[activeModule] || ""}
          </h2>
          <div className="ml-auto flex items-center gap-3">
            <NotificationBell onNavigate={handleModuleChange} />
            {user && (
              <div className="flex items-center gap-2 text-[11px] font-sans text-hg-text-dim">
                <span className="hidden sm:inline">{user.name || user.email}</span>
                <button
                  data-testid="header-logout-btn"
                  onClick={logout}
                  className="px-2.5 py-1 border border-hg-border rounded-sm hover:border-hg-green hover:text-hg-green transition-colors"
                >Sign out</button>
              </div>
            )}
          </div>
        </header>
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          {renderModule()}
        </div>
      </main>
    </div>
  );
}

function AuthedShell() {
  const { isLoaded, isSignedIn } = useAuth();

  // Wait for Clerk to initialize before deciding what to render.
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-hg-bg flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-hg-green" />
      </div>
    );
  }

  // Clerk owns the OAuth callback internally — no hash handling needed here.
  if (!isSignedIn) return <Login />;
  return <HubApp />;
}

// The public review form renders OUTSIDE the Clerk-gated shell (no login), so
// customers reach it via a link. Everything else stays behind AuthProvider.
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/review" element={<PublicReviewForm />} />
        <Route path="/sign" element={<FitterSigning />} />
        <Route path="/add-card" element={<CardSetup />} />
        <Route
          path="*"
          element={
            <AuthProvider>
              <AuthedShell />
            </AuthProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
